import * as Sentry from "@sentry/react"

const hasIOSNativeBridge = () =>
  !!(window as any).webkit?.messageHandlers?.IOS_BRIDGE

const hasAndroidNativeBridge = () => !!(window as any).AndroidBridge

const isIOSWebViewByUserAgent = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera
  const isIOS = /iPhone|iPad|iPod/.test(userAgent)
  const isWebViewUA = /AppleWebKit/.test(userAgent) && !/Safari/.test(userAgent)

  return isIOS && isWebViewUA
}

const isAndroidWebViewByUserAgent = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera

  return /Android/i.test(userAgent) && /wv/i.test(userAgent)
}

export type WebViewDetectionSignals = {
  ios: { bridge: boolean; ua: boolean }
  android: { bridge: boolean; ua: boolean }
}

export const getWebViewDetectionSignals = (): WebViewDetectionSignals => ({
  ios: { bridge: hasIOSNativeBridge(), ua: isIOSWebViewByUserAgent() },
  android: {
    bridge: hasAndroidNativeBridge(),
    ua: isAndroidWebViewByUserAgent(),
  },
})

let mismatchAlreadyLogged = false

const logSignalMismatchOnce = (signals: WebViewDetectionSignals) => {
  const mismatch =
    signals.ios.bridge !== signals.ios.ua ||
    signals.android.bridge !== signals.android.ua
  if (!mismatch || mismatchAlreadyLogged) return
  mismatchAlreadyLogged = true

  Sentry.withScope((scope) => {
    scope.setFingerprint(["isInWebView", "signal-mismatch"])
    scope.setContext("webview_detection", {
      ...signals,
      userAgent: navigator.userAgent,
    })
    Sentry.captureMessage(
      "[isInWebView/mismatch] bridge-presence and UA heuristic disagree",
      "warning",
    )
  })
}

export function isIOSWebView() {
  const signals = getWebViewDetectionSignals()
  logSignalMismatchOnce(signals)
  return signals.ios.bridge || signals.ios.ua
}

export const isAndroidWebView = () => {
  const signals = getWebViewDetectionSignals()
  logSignalMismatchOnce(signals)
  return signals.android.bridge || signals.android.ua
}

export const isInWebView = () => {
  return isIOSWebView() || isAndroidWebView()
}
